import * as v from "valibot";
import {
  types,
  query_recursive_types as RpcRecursiveTypes,
} from "@/infra/api/rpc/api";
import { filterUndefined } from "@/utils/filter-undefined";

type SeriesResponse = RpcRecursiveTypes.ISeriesResponse;

export const ScriptType = {
  Chat: "CHAT",
  Novel: "NOVEL",
} as const;
export type ScriptType = (typeof ScriptType)[keyof typeof ScriptType];

export const ISharedWithStatus = {
  public: "public",
  follower: "follower",
  mutualFollower: "mutual-follower",
  unknown: "unknown",
} as const;
export type SharedWithStatus =
  (typeof ISharedWithStatus)[keyof typeof ISharedWithStatus];

export const SeriesListItemSchema = v.object({
  id: v.string(),
  title: v.string(),
  scriptType: v.enum(ScriptType),
  description: v.optional(v.string()),
  tags: v.array(v.string()),
  isCompleted: v.boolean(),
  isOneshot: v.boolean(),
  isSensitive: v.boolean(),
  isPublic: v.boolean(),
  sharedWithStatus: v.enum(ISharedWithStatus),
  thumbnailUrl: v.optional(v.string()),
});

export type ISeriesListItem = v.InferOutput<typeof SeriesListItemSchema>;

/**
 * @throws {v.ValiError}
 */
export const convertSeriesResponseToISeriesListItem = (
  data: SeriesResponse,
): ISeriesListItem => {
  return v.parse(SeriesListItemSchema, {
    id: data.id?.value,
    title: data.title?.value ?? "",
    description: data.description?.value ?? "",
    tags: data.tags?.map((tag) => tag.value)?.filter(filterUndefined) ?? [],
    isCompleted: data.isCompleted?.value ?? false,
    isOneshot: data.isOneshot?.value ?? false,
    isSensitive: (data.sensitiveStoryCount?.value ?? 0) > 0,
    isPublic: (data.searchableStoryCount?.value ?? 0) > 0,
    scriptType:
      data.hasChatNovelScriptStory?.value === true
        ? ScriptType.Novel
        : ScriptType.Chat,
    sharedWithStatus: convertSharedWithStatus(data.sharedWithStatus),
    thumbnailUrl: data.thumbnail?.servingUrl?.value ?? undefined,
  });
};

export const convertSeriesResponseToISeriesListItemOrUndefined = (
  data: SeriesResponse,
): ISeriesListItem | undefined => {
  try {
    return convertSeriesResponseToISeriesListItem(data);
  } catch (error) {
    console.error("IContestListItem convert error:", error);
  }
  return undefined;
};

const convertSharedWithStatus = (
  sharedWithStatus: types.SharedWithStatus | null | undefined,
): SharedWithStatus => {
  switch (sharedWithStatus) {
    case types.SharedWithStatus.SHARED_WITH_PUBLIC: {
      return "public";
    }
    case types.SharedWithStatus.SHARED_WITH_FOLLOWER: {
      return "follower";
    }
    case types.SharedWithStatus.SHARED_WITH_MUTUAL_FOLLOWER: {
      return "mutual-follower";
    }
    default: {
      return "unknown";
    }
  }
};
